import type { IconName } from "@/components/ui/icon";

/**
 * Primary sidebar navigation (frontend.spec §14, extended for AOS v0.2).
 *
 * `to` is an absolute portal route. Dashboard and Operations are custom pages;
 * OKRs points at the objectives resource list; the v0.2 "Context Atlas" group
 * targets the new context-centric resources + custom routes (`/contexts`,
 * `/signals`, `/package-graph`, `/bets`, `/frames`, `/principles`,
 * `/artifacts`). The rest are Refine resource list routes. This config is
 * intentionally self-contained so the layout does not depend on the D1-owned
 * `lib/routes.ts` during the parallel build phase.
 *
 * `section` groups items under a heading in the sidebar; items without a
 * `section` render ungrouped at the top (back-compat with the v0.1 flat list).
 */
export interface NavItem {
  label: string;
  to: string;
  /** Semantic icon key resolved by the central icon map (`@/components/ui/icon`). */
  icon: IconName;
  /** Optional sidebar group heading. Omit for the top, ungrouped block. */
  section?: string;
}

export const NAV_SECTION_CONTEXT = "Context Atlas";
export const NAV_SECTION_OPERATE = "Operate";
export const NAV_SECTION_AUDIT = "Audit";
export const NAV_SECTION_ADMIN = "Admin";
export const NAV_SECTION_REFERENCE = "Reference";
// S14 (Wave 4b): the "Surface" group — actor/lens authoring & explainer
// surfaces (Access Lens, Lens Builder). Matches the layout-reference rail.
export const NAV_SECTION_SURFACE = "Surface";

export const NAV_ITEMS: NavItem[] = [
  // Top, ungrouped — the v0.1 working set.
  { label: "Dashboard", to: "/dashboard", icon: "dashboard" },
  // S05 (Wave 4c): the founder/top-management portfolio landing — bet/KR
  // movement, decisions awaiting me, blockers, deliverables.
  { label: "Executive Cockpit", to: "/executive", icon: "operations" },
  { label: "Projects", to: "/projects", icon: "projects" },
  { label: "Tasks", to: "/tasks", icon: "tasks" },
  { label: "OKRs", to: "/objectives", icon: "okrs" },
  { label: "Artifacts", to: "/artifacts", icon: "artifacts", section: NAV_SECTION_CONTEXT },
  { label: "Context Atlas", to: "/contexts", icon: "contexts", section: NAV_SECTION_CONTEXT },
  { label: "Signals", to: "/signals", icon: "signals", section: NAV_SECTION_CONTEXT },
  { label: "Package Graph", to: "/package-graph", icon: "packageGraph", section: NAV_SECTION_CONTEXT },
  { label: "Bets", to: "/bets", icon: "bets", section: NAV_SECTION_CONTEXT },
  { label: "Frames", to: "/frames", icon: "frames", section: NAV_SECTION_CONTEXT },
  { label: "Principles", to: "/principles", icon: "principles", section: NAV_SECTION_CONTEXT },
  // S14: cross-package read projection (sits alongside Packages/Bets/Frames).
  { label: "Package Catalog", to: "/package-catalog", icon: "packages", section: NAV_SECTION_CONTEXT },
  { label: "Skills", to: "/skills", icon: "skills", section: NAV_SECTION_OPERATE },
  { label: "Processes", to: "/processes", icon: "processes", section: NAV_SECTION_OPERATE },
  { label: "Rules", to: "/rules", icon: "rules", section: NAV_SECTION_OPERATE },
  { label: "Evidence", to: "/evidence", icon: "evidence", section: NAV_SECTION_OPERATE },
  { label: "Decisions", to: "/decisions", icon: "decisions", section: NAV_SECTION_OPERATE },
  { label: "Documents", to: "/documents", icon: "documents", section: NAV_SECTION_OPERATE },
  { label: "Operations", to: "/operations", icon: "operations", section: NAV_SECTION_OPERATE },
  // S14: cross-context "what needs me, everywhere" ranked attention stream.
  { label: "Attention Queue", to: "/attention-queue", icon: "actionCards", section: NAV_SECTION_OPERATE },
  // S18 (Wave 4c): access-filtered retrieval workbench — ask a question, get an
  // answer composed over authorized truth with every source/trace shown.
  { label: "Knowledge Assistant", to: "/knowledge/assistant", icon: "assistant", section: NAV_SECTION_OPERATE },
  // Audit group (design-system §4.1) — the inspectability surface: the raw
  // event log (the Event Stream) + the kernel/trace tables (rule-runs,
  // agent-runs) + the runtime/observability [PROPOSED] modules, made navigable
  // (not just addressable) per frontend-entity-coverage §5.
  { label: "Event Stream", to: "/events", icon: "events", section: NAV_SECTION_AUDIT },
  { label: "Rule Runs", to: "/rule-runs", icon: "ruleRuns", section: NAV_SECTION_AUDIT },
  { label: "Agent Runs", to: "/agent-runs", icon: "agentRuns", section: NAV_SECTION_AUDIT },
  { label: "Event Sources", to: "/event-sources", icon: "eventSources", section: NAV_SECTION_AUDIT },
  { label: "Effect Runs", to: "/effect-runs", icon: "effectRuns", section: NAV_SECTION_AUDIT },
  {
    label: "Transition Proposals",
    to: "/transition-proposals",
    icon: "transitionProposals",
    section: NAV_SECTION_AUDIT,
  },
  {
    label: "Contour Instances",
    to: "/contour-instances",
    icon: "contourInstances",
    section: NAV_SECTION_AUDIT,
  },
  {
    label: "Action Cards",
    to: "/action-cards",
    icon: "actionCards",
    section: NAV_SECTION_AUDIT,
  },
  // S13 (Wave 4a): the Approval Center — the human authority surface where
  // pending two-party approval gates are decided. (The Trace Viewer and Packet
  // Inspector are id-keyed drill surfaces reached from a run/context, so they
  // have no top-level nav entry.)
  {
    label: "Approval Center",
    to: "/approvals",
    icon: "approve",
    section: NAV_SECTION_AUDIT,
  },
  // Admin group — the org/identity entities (Milestone A: companies, users,
  // roles, project_members). Made navigable so no resource is URL-only
  // (frontend-entity-coverage §5 / §6.1).
  { label: "Companies", to: "/companies", icon: "companies", section: NAV_SECTION_ADMIN },
  { label: "Users", to: "/users", icon: "users", section: NAV_SECTION_ADMIN },
  { label: "Roles", to: "/roles", icon: "roles", section: NAV_SECTION_ADMIN },
  { label: "Permissions", to: "/permissions", icon: "decisions", section: NAV_SECTION_ADMIN },
  // Reference group — the in-app spec/docs reader (FRONTEND-FOLLOWUP §C). The
  // canonical + implementation specs are the single source of truth, surfaced
  // navigable (not URL-only) per frontend-entity-coverage §5.
  { label: "Documentation", to: "/docs", icon: "docs", section: NAV_SECTION_REFERENCE },
  // S14 (Wave 4b) Surface group — the actor/lens authoring & explainer
  // surfaces. The Access Lens index renders a "choose an actor" empty state;
  // the Lens Builder is the lens catalog. (The other S14 screens — Project
  // Control Tower, Context Explorer, Strategy Map, Skill Workspace, Contour
  // Board, Actor Passport — are id-keyed drill surfaces reached via a button
  // from their owning record, so they carry no top-level nav entry, mirroring
  // the Decision Room / Trace Viewer / Control Room / Packet Inspector.)
  { label: "Access Lens", to: "/access-lens", icon: "scope", section: NAV_SECTION_SURFACE },
  { label: "Lens Builder", to: "/lens-builder", icon: "frames", section: NAV_SECTION_SURFACE },
];
